import { Injectable, NgZone } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { AuthService } from 'app/core/auth/auth.service';
import { BehaviorSubject, fromEvent, merge, Subscription, timer } from 'rxjs';
import { filter, throttleTime } from 'rxjs/operators';

@Injectable({
    providedIn: 'root',
})
export class AutoLogoutService {
    private inactivityTimer: Subscription | null = null;
    private lockScreenTimer: Subscription | null = null;
    private userActivitySubscription: Subscription | null = null;
    private inactivityTimeout = 600000; // 10 minutes in milliseconds
    private lockScreenTimeout = 300000; // 5 minutes in milliseconds
    private countdownSubject = new BehaviorSubject<number>(0); // Countdown observable
    private lastRoute: string = '/'; // Default to root if undefined
    constructor(private router: Router,
        private ngZone: NgZone,
        private _authService: AuthService) {

        this.router.events
            .pipe(filter((event) => event instanceof NavigationEnd))
            .subscribe((event: NavigationEnd) => {
                if (this.router.url !== '/confirmation-required') {
                    this.lastRoute = event.urlAfterRedirects;
                }
            });
    }

    // Access countdown as an observable
    getCountdown() {
        return this.countdownSubject.asObservable();
    }

    startMonitoring() {
        this.resetTimer();
        this.monitorUserActivity();
    }

    private monitorUserActivity() {
        // Bubbling events can be observed straight off `document`. `scroll` does NOT
        // bubble, so it's observed in the capture phase to catch scrolling inside any
        // nested scroll container (report pages, dialogs, tables, …) — otherwise a
        // user reading/scrolling a long page registers as "idle" and gets logged out.
        const bubblingEvents = ['mousemove', 'mousedown', 'keydown', 'click', 'touchstart', 'wheel', 'input'];
        const streams = bubblingEvents.map(event => fromEvent(document, event));
        streams.push(fromEvent(document, 'scroll', { capture: true } as any));

        this.userActivitySubscription = merge(...streams)
            .pipe(throttleTime(1000)) // Throttle to avoid excessive resets
            .subscribe(() => {
                this.resetTimer();
            });
    }

    private resetTimer() {
        if (this.router.url === '/confirmation-required') {
            // On the lock screen the 5-minute countdown owns the clock; don't let
            // background activity restart the 10-minute inactivity timer here.
            return;
        }

        this.armInactivityTimer();
    }

    /** (Re)starts the 10-minute inactivity timer regardless of the current route. */
    private armInactivityTimer() {
        if (this.inactivityTimer) {
            this.inactivityTimer.unsubscribe();
        }

        this.inactivityTimer = timer(this.inactivityTimeout).subscribe(() => {
            this.navigateToLockScreen();
        });
    }

    private navigateToLockScreen() {
        this.router.navigate(['/confirmation-required']);
        this.startLockScreenTimer();
    }

    private startLockScreenTimer() {
        if (this.lockScreenTimer) {
            this.lockScreenTimer.unsubscribe();
        }

        // Initialize countdown value
        let countdown = this.lockScreenTimeout / 1000;

        // Emit countdown every second
        this.lockScreenTimer = timer(0, 1000).subscribe(() => {
            if (countdown <= 0) {
                this.lockScreenTimer?.unsubscribe();
                this.logoutUser();
            } else {
                this.countdownSubject.next(--countdown);
            }
        });
    }

    public continueSession() {
        if (this.lockScreenTimer) {
            this.lockScreenTimer.unsubscribe();
        }
        // Arm directly: at this point the URL is still '/confirmation-required', so
        // resetTimer() would no-op via its guard and leave the session unprotected
        // until the next stray event. The component navigates away right after.
        this.armInactivityTimer();
    }

    private logoutUser() {
        this._authService.signOut().subscribe();
        this.router.navigate(['/sign-in']); // Navigate to login on logout
    }


    stopMonitoring() {
        if (this.inactivityTimer) {
            this.inactivityTimer.unsubscribe();
        }
        if (this.userActivitySubscription) {
            this.userActivitySubscription.unsubscribe();
        }
        if (this.lockScreenTimer) {
            this.lockScreenTimer.unsubscribe();
        }
    }

    public getLastRoute(): string {
        return this.lastRoute;
    }
}
